import type { GameProjection, LegalCommand } from '@lafayette/protocol';

export type AlertKind = 'turn' | 'action' | 'input';

type ForcedCommand = Extract<
  LegalCommand,
  { type: 'respondToAction' | 'payDebt' | 'placeUpgrade' | 'discardCards' }
>;

function forcedCommandKey(command: ForcedCommand, projection: GameProjection): string {
  switch (command.type) {
    case 'respondToAction':
      return `respond:${projection.pendingAction?.instanceId ?? projection.version}`;
    case 'payDebt':
      return `pay:${command.debtId}`;
    case 'placeUpgrade':
      return `upgrade:${command.cardInstanceId}`;
    case 'discardCards':
      return `discard:${projection.discardRequired?.playerId ?? projection.self.id}`;
  }
}

function inputRequestKey(projection: GameProjection): string | null {
  const keys = projection.legalCommands.flatMap((command) => {
    switch (command.type) {
      case 'respondToAction':
      case 'payDebt':
      case 'placeUpgrade':
      case 'discardCards':
        return [forcedCommandKey(command, projection)];
      default:
        return [];
    }
  });
  return keys.length > 0 ? keys.sort().join('|') : null;
}

/**
 * Finds newly relevant events without replaying alerts when the same
 * projection is rebroadcast for presence changes or restored after a refresh.
 */
export function alertsForTransition(
  previous: GameProjection | null,
  next: GameProjection,
): AlertKind[] {
  if (!previous) {
    return next.version === 0 && next.turn.playerId === next.self.id ? ['turn'] : [];
  }
  if (previous.matchId !== next.matchId || next.version <= previous.version) return [];

  const alerts: AlertKind[] = [];
  if (previous.turn.playerId !== previous.self.id && next.turn.playerId === next.self.id) {
    alerts.push('turn');
  }

  const pendingActionStarted =
    next.pendingAction !== null &&
    next.pendingAction.affected.includes(next.self.id) &&
    next.pendingAction.instanceId !== previous.pendingAction?.instanceId;
  const impactPresented = next.presentation.some((cue) => cue.type === 'actionAffectedYou');
  if (pendingActionStarted || impactPresented) alerts.push('action');

  const previousInput = inputRequestKey(previous);
  const nextInput = inputRequestKey(next);
  if (nextInput !== null && nextInput !== previousInput) alerts.push('input');

  return alerts;
}

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  context ??= new window.AudioContext();
  return context;
}

function scheduleTone(
  audio: AudioContext,
  frequency: number,
  start: number,
  duration: number,
  gainLevel: number,
  type: OscillatorType = 'sine',
): void {
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(gainLevel, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain);
  gain.connect(audio.destination);
  oscillator.start(start);
  oscillator.stop(start + duration);
}

function scheduleAlert(audio: AudioContext, gainLevel: number): void {
  if (gainLevel <= 0) return;
  const start = audio.currentTime + 0.025;
  scheduleTone(audio, 523.25, start, 0.16, gainLevel);
  scheduleTone(audio, 783.99, start + 0.14, 0.24, gainLevel);
}

export function alertVolumeGain(volume: number): number {
  const normalizedVolume = Number.isFinite(volume) ? Math.min(100, Math.max(0, volume)) / 100 : 0.5;
  return 0.125 * normalizedVolume ** Math.log2(5);
}

/** Called from a user gesture so browsers permit later notification sounds. */
export function unlockAlertSounds(): void {
  const audio = audioContext();
  if (audio?.state === 'suspended') void audio.resume().catch(() => {});
}

/**
 * Arms persisted sound settings after a reload. The first pointer or keyboard
 * interaction unlocks Web Audio without making a noise of its own.
 */
export function armAlertSounds(): () => void {
  if (typeof document === 'undefined') return () => {};

  const unlock = (): void => {
    unlockAlertSounds();
    cleanup();
  };
  const cleanup = (): void => {
    document.removeEventListener('pointerdown', unlock, true);
    document.removeEventListener('keydown', unlock, true);
  };
  document.addEventListener('pointerdown', unlock, true);
  document.addEventListener('keydown', unlock, true);
  return cleanup;
}

export function playAlertSound(volume: number): void {
  const audio = audioContext();
  if (!audio) return;
  const gainLevel = alertVolumeGain(volume);
  if (audio.state === 'running') {
    scheduleAlert(audio, gainLevel);
    return;
  }
  void audio
    .resume()
    .then(() => {
      if (audio.state === 'running') scheduleAlert(audio, gainLevel);
    })
    .catch(() => {});
}
